'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@heroui/button';
import { Input } from '@heroui/input';
import Select from "react-select";
import { TzSelectStyles } from '@/shared/styleDefs';
import { AUTH_ARCHITECTURE_OPTIONS, OrganizationDTO, UpdateOrganizationRequest } from '@/shared/types';
const STATUS_OPTIONS = [{
  value: 'active',
  label: 'Active'
}, {
  value: 'inactive',
  label: 'Inactive'
}];
export interface EditOrganizationFormProps {
  organization: OrganizationDTO;
  onSubmit: (orgId: string, data: UpdateOrganizationRequest) => Promise<OrganizationDTO>;
  onSuccess: (org: OrganizationDTO) => void;
  onCancel: () => void;
}
export function EditOrganizationForm({
  organization,
  onSubmit,
  onSuccess,
  onCancel
}: EditOrganizationFormProps) {
  const [formData, setFormData] = useState<UpdateOrganizationRequest>({
    name: organization.name,
    contact_email: organization.contact_email ?? 'support@tranquility-ai.com',
    auth_architecture: organization.auth_architecture ?? 'cognito-multi-tenant',
    domain: organization.domain ?? '',
    status: organization.status ?? 'active',
    cognito_user_pool_arn: organization.cognito_user_pool_arn ?? '',
    cognito_domain: organization.cognito_domain ?? '',
    app_client_id: organization.app_client_id ?? ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setFormData({
      name: organization.name,
      contact_email: organization.contact_email ?? 'support@tranquility-ai.com',
      auth_architecture: organization.auth_architecture ?? 'cognito-multi-tenant',
      domain: organization.domain ?? '',
      status: organization.status ?? 'active',
      cognito_user_pool_arn: organization.cognito_user_pool_arn ?? '',
      cognito_domain: organization.cognito_domain ?? '',
      app_client_id: organization.app_client_id ?? ''
    });
  }, [organization]);
  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setError(null);
      const updated = await onSubmit(organization.id, formData);
      onSuccess(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update organization');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-sentry-component="EditOrganizationForm" data-sentry-source-file="EditOrganizationForm.tsx">
      {error && <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 text-red-300 text-sm">
          {error}
        </div>}

      <div className="bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-purple-300 mb-4">Basic Information</h2>
        <Input aria-label="Organization Name" label="Organization Name" labelPlacement="outside" name="name" value={formData.name} onValueChange={v => handleChange('name', v)} isRequired placeholder="e.g., City of Springfield" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="EditOrganizationForm.tsx" />
        <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">Auth Architecture *</label>
        <Select aria-label="Auth Architecture" key="auth_architecture-select" styles={{
        ...TzSelectStyles,
        menuPortal: base => ({
          ...base,
          zIndex: 9999,
          color: 'black'
        })
      }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} required className="react-select-container font-inter pr-2 w-full custom-scrollbar2" value={formData.auth_architecture ? {
        value: formData.auth_architecture,
        label: AUTH_ARCHITECTURE_OPTIONS.find(o => o.value === formData.auth_architecture)?.label ?? formData.auth_architecture
      } : null} onChange={(newValue: unknown) => {
        const option = newValue as {
          value: string;
          label: string;
        } | null;
        handleChange('auth_architecture', option?.value ?? '');
      }} options={AUTH_ARCHITECTURE_OPTIONS.map(option => ({
        value: option.value,
        label: option.label
      }))} noOptionsMessage={() => "No options available"} data-sentry-element="Select" data-sentry-source-file="EditOrganizationForm.tsx" />
        <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">Status *</label>
        <Select aria-label="Status" key="status-select" styles={{
        ...TzSelectStyles,
        menuPortal: base => ({
          ...base,
          zIndex: 9999,
          color: 'black'
        })
      }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" placeholder="Select status" value={formData.status ? {
        value: formData.status,
        label: STATUS_OPTIONS.find(o => o.value === formData.status)?.label ?? formData.status
      } : null} onChange={(newValue: unknown) => {
        const option = newValue as {
          value: string;
          label: string;
        } | null;
        handleChange('status', option?.value ?? '');
      }} options={STATUS_OPTIONS.map(option => ({
        value: option.value,
        label: option.label
      }))} noOptionsMessage={() => "No options available"} data-sentry-element="Select" data-sentry-source-file="EditOrganizationForm.tsx" />
      </div>

      <div className="bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-purple-300 mb-4">Cognito Configuration</h2>
        <Input label="Cognito Domain (optional)" aria-label="Cognito Domain (optional)" labelPlacement="outside" name="cognito_domain" value={formData.cognito_domain ?? ''} onValueChange={v => handleChange('cognito_domain', v)} placeholder="dev-timepilot02-auth" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="EditOrganizationForm.tsx" />
        <Input label="Cognito User Pool ARN (optional)" aria-label="Cognito User Pool ARN (optional)" name="cognito_user_pool_arn" value={formData.cognito_user_pool_arn ?? ''} onValueChange={v => handleChange('cognito_user_pool_arn', v)} placeholder="arn:aws:cognito-idp:us-east-1:..." classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="EditOrganizationForm.tsx" />
        <Input label="Cognito App Client ID (optional)" aria-label="Cognito App Client ID (optional)" name="app_client_id" value={formData.app_client_id ?? ''} onValueChange={v => handleChange('app_client_id', v)} placeholder="" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="EditOrganizationForm.tsx" />
      </div>

      <div className="bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-2">
        <h2 className="text-xl font-semibold text-purple-300 mb-4">Read-Only Information</h2>
        <p className="text-sm text-slate-300">ID: <span className="font-mono text-white">{organization.id}</span></p>
        {organization.external_id && <p className="text-sm text-slate-300">External ID: <span className="font-mono text-white">{organization.external_id}</span></p>}
        <p className="text-sm text-slate-300">Created: {organization.created_at ? new Date(organization.created_at).toLocaleString() : '—'}</p>
        <p className="text-sm text-slate-300">Updated: {organization.updated_at ? new Date(organization.updated_at).toLocaleString() : '—'}</p>
      </div>

      <div className="flex justify-end gap-4 pt-2">
        <Button type="button" variant="light" onPress={onCancel} className="text-slate-300 hover:text-white" data-sentry-element="Button" data-sentry-source-file="EditOrganizationForm.tsx">
          Cancel
        </Button>
        <Button type="submit" isLoading={isSubmitting} isDisabled={!formData.name?.trim()} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg" data-sentry-element="Button" data-sentry-source-file="EditOrganizationForm.tsx">
          Save Changes
        </Button>
      </div>
    </form>;
}